import type {CampoDeFinanciacion, CampoDeTramite} from '@cnc/contracts';
import {txt, type Texto} from '../i18n/idioma';

// Notas de ayuda de la ficha de una exportacion (Daniel, 14/10/2026): las
// tablas y secciones del detalle (facturas, contenedores, Big Bags, logistica,
// aduana, financiacion, documentos, asignacion, expediente) y la carga de
// cambios desde Excel. Los campos del seguimiento estan en ayuda-seguimiento.

const GEX = txt('Sale del GEX FR08; se edita aquí.', 'It comes from the GEX FR08; it is edited here.');
const conGex = (es: string, en: string): Texto => txt(`${es} ${GEX.es}`, `${en} ${GEX.en}`);
const CADENA = txt(
  'Del Intercompany Export Chain; se edita aquí.',
  'From the Intercompany Export Chain; it is edited here.',
);
const deCadena = (es: string, en: string): Texto => txt(`${es} ${CADENA.es}`, `${en} ${CADENA.en}`);
const STATUS_BLQ = txt('Del Status BLQ; se edita aquí.', 'From the Status BLQ; it is edited here.');
const deStatusBlq = (es: string, en: string): Texto => txt(`${es} ${STATUS_BLQ.es}`, `${en} ${STATUS_BLQ.en}`);

// Tablas de la ficha.
export const AYUDA_DETALLE = {
  facturaNumero: txt(
    'Número de la factura: el de la columna Invoice Number del Status o el que se escribe al generar los documentos de aduana.',
    'Invoice number: the one in the Invoice Number column of the Status or the one typed when generating the customs documents.',
  ),
  facturaFecha: txt(
    'Fecha de la factura: columna Invoice Date del Consolidated Shipments o la de los documentos de aduana.',
    'Invoice date: Invoice Date column of the Consolidated Shipments or the one from the customs documents.',
  ),
  facturaAnio: txt(
    'Año de la factura (columna Invoice Year del Consolidated Shipments).',
    'Invoice year (Invoice Year column of the Consolidated Shipments).',
  ),
  facturaMina: txt(
    'Mina facturada, cuando la factura es por mina; por eso una exportación puede tener varias.',
    'Invoiced mine, when the invoice is per mine; that is why an export can have several.',
  ),
  facturaMonto: txt('Valor de la factura en dólares.', 'Invoice amount in dollars.'),
  contenedorNumero: txt(
    'Número del contenedor registrado para la exportación.',
    'Number of the container recorded for the export.',
  ),
  contenedorOrden: txt('Orden del contenedor dentro de la exportación.', 'Order of the container within the export.'),
  contenedorBigBags: txt(
    'Big Bags del inventario que van en ese contenedor. Los cuenta la app.',
    'Big Bags from inventory in that container. The app counts them.',
  ),
  contenedorWmt: txt(
    'Toneladas húmedas: suma de los Big Bags del contenedor.',
    'Wet tonnes: sum of the container’s Big Bags.',
  ),
  contenedorDmt: txt(
    'Toneladas secas: suma de los Big Bags del contenedor.',
    'Dry tonnes: sum of the container’s Big Bags.',
  ),
  bbNo: txt(
    'Número del Big Bag (columna BB # del Consolidated Shipments). Su prefijo define mina, proveedor y exportador.',
    'Big Bag number (BB # column of the Consolidated Shipments). Its prefix sets the mine, supplier and exporter.',
  ),
  bbEstado: txt(
    'Disponible, Asignado o Exportado. Lo lleva la app: pasa a exportado cuando tiene fecha de llenado o la exportación ya salió.',
    'Available, Assigned or Exported. The app keeps it: it becomes exported when it has a filling date or the export has left.',
  ),
  bbProveedor: txt(
    'Proveedor del Big Bag; lo define el prefijo de su número.',
    'Supplier of the Big Bag; set by the prefix of its number.',
  ),
  bbMina: txt('Mina de origen; la define el prefijo del número.', 'Mine of origin; set by the number prefix.'),
  bbContenedor: txt(
    'Contenedor donde va la bolsa: del Consolidated Shipments, del Seguimiento Concentrados o de la asignación por reserva.',
    'Container the bag goes in: from the Consolidated Shipments, the Seguimiento Concentrados or the booking assignment.',
  ),
  bbSello: txt('Sello del Big Bag, del Consolidated Shipments.', 'Big Bag seal, from the Consolidated Shipments.'),
  bbWmt: txt(
    'Peso húmedo del Big Bag en toneladas, del Consolidated Shipments.',
    'Wet weight of the Big Bag in tonnes, from the Consolidated Shipments.',
  ),
  bbDmt: txt(
    'Peso seco del Big Bag en toneladas, del Consolidated Shipments.',
    'Dry weight of the Big Bag in tonnes, from the Consolidated Shipments.',
  ),
  bbAuG: txt(
    'Gramos de oro del Big Bag, del Consolidated Shipments.',
    'Grams of gold in the Big Bag, from the Consolidated Shipments.',
  ),
  bbAgG: txt(
    'Gramos de plata del Big Bag, del Consolidated Shipments.',
    'Grams of silver in the Big Bag, from the Consolidated Shipments.',
  ),
  historialFecha: txt('Cuándo cambió la fecha.', 'When the date changed.'),
  historialCampo: txt(
    'Qué fecha cambió: ETD, ETA Mundra, ETA Sachana, ETA Hindustan, ETA inicial o ETA destino.',
    'Which date changed: ETD, ETA Mundra, ETA Sachana, ETA Hindustan, initial ETA or destination ETA.',
  ),
  historialAntes: txt('El valor que tenía antes del cambio.', 'The value before the change.'),
  historialDespues: txt('El valor nuevo.', 'The new value.'),
  historialOrigen: txt(
    'De dónde vino el cambio: un archivo, la pantalla, la migración o la página de la naviera.',
    'Where the change came from: a file, the screen, the migration or the shipping line website.',
  ),
  valoresOriginales: txt(
    'Lo que traía el archivo de seguimiento en un campo antes de limpiarlo (N/A, fechas de 1900, textos en un número). Lo guarda la app al cargar.',
    'What the tracking file had in a field before it was cleaned up (N/A, 1900 dates, text in a number). The app keeps it on upload.',
  ),
  adicionales: txt(
    'Columnas del Status que todavía no tienen lugar propio en la app (pagos, cartas de crédito, observaciones…). Se guardan tal cual vienen.',
    'Status columns that do not have their own place in the app yet (payments, letters of credit, remarks…). They are kept as they come.',
  ),
} satisfies Record<string, Texto>;

// Logistica por contenedor (archivo Seguimiento Concentrados).
export const AYUDA_LOGISTICA = {
  bl: txt(
    'Número del BL (conocimiento de embarque), con su fecha de emisión y la del switch si la hubo. Del Seguimiento Concentrados.',
    'BL (bill of lading) number, with its issue date and the switch date if any. From the Seguimiento Concentrados.',
  ),
  bigBags: txt(
    'Big Bags del contenedor según el Seguimiento Concentrados. Si la app tiene otra cantidad, sale el aviso ⚠.',
    'Big Bags in the container according to the Seguimiento Concentrados. If the app has a different number, the ⚠ warning shows.',
  ),
  itr: txt(
    'Peso del contenedor al cargarlo (ITR), en kilos. Del Seguimiento Concentrados.',
    'Container weight at loading (ITR), in kilos. From the Seguimiento Concentrados.',
  ),
  refineria: txt(
    'Peso del contenedor al llegar a la refinería, en kilos. Del Seguimiento Concentrados.',
    'Container weight on arrival at the refinery, in kilos. From the Seguimiento Concentrados.',
  ),
  diferencia: txt(
    'Peso de llegada a la refinería menos peso del cargue (ITR). Lo calcula la app.',
    'Refinery arrival weight minus loading weight (ITR). The app works it out.',
  ),
  inspeccion: txt(
    'Inspección del contenedor: la de la Policía viene del Seguimiento Concentrados; la selectividad de la DIAN, del GEX FR08.',
    'Container inspection: the Police one comes from the Seguimiento Concentrados; the DIAN selectivity, from the GEX FR08.',
  ),
  recorrido: txt(
    'Fecha de cada paso del contenedor, desde la planta hasta la refinería. Del Seguimiento Concentrados.',
    'Date of each step of the container, from the plant to the refinery. From the Seguimiento Concentrados.',
  ),
  sellos: txt(
    'Sellos del contenedor: el anterior y el nuevo cuando se cambió en una inspección. Del Seguimiento Concentrados.',
    'Container seals: the previous and the new one when it was changed at an inspection. From the Seguimiento Concentrados.',
  ),
  contenido: txt(
    'Lo que lleva el contenedor de cada proveedor: Big Bags, kilos húmedos y secos, gramos de oro y de plata. Del Seguimiento Concentrados.',
    'What the container carries from each supplier: Big Bags, wet and dry kilos, grams of gold and silver. From the Seguimiento Concentrados.',
  ),
  sinContenedor: txt(
    'Filas del Seguimiento Concentrados de esta exportación que todavía no dicen en qué contenedor van.',
    'Rows of the Seguimiento Concentrados for this export that do not yet say which container they go in.',
  ),
} satisfies Record<string, Texto>;

// Tramite de aduana (GEX FR08), por campo.
export const AYUDA_TRAMITE: Record<CampoDeTramite, Texto> = {
  numeroExportacion: conGex('Número de la exportación en el GEX (No. EXP).', 'Export number in the GEX (No. EXP).'),
  tipoProducto: conGex('Tipo de producto declarado (concentrados).', 'Declared product type (concentrates).'),
  paisDestino: conGex('País de destino declarado.', 'Declared destination country.'),
  vuceGeneracion: conGex('Fecha en que se generó la solicitud en la VUCE.', 'Date the VUCE request was generated.'),
  vucePago: conGex('Fecha de pago de la VUCE.', 'Date the VUCE was paid.'),
  vuceRadicado: conGex('Número de radicado de la VUCE.', 'VUCE filing number.'),
  vuceAprobacion: conGex(
    'Número de aprobación de la VUCE (visto bueno de la ANM).',
    'VUCE approval number (ANM clearance).',
  ),
  vuceFechaAprobacion: conGex('Fecha de aprobación de la VUCE.', 'VUCE approval date.'),
  agenciaAduanas: conGex('Agencia de aduanas que hace el trámite.', 'Customs agency handling the filing.'),
  saeFecha: conGex(
    'Fecha de la SAE (solicitud de autorización de embarque).',
    'Date of the SAE (shipment authorisation request).',
  ),
  saeNumero: conGex('Número de la SAE.', 'SAE number.'),
  agenciaCarga: conGex('Agencia de carga (forwarder).', 'Freight agency (forwarder).'),
  solicitudReserva: conGex(
    'Fecha en que se le pidió la reserva al forwarder.',
    'Date the booking was requested from the forwarder.',
  ),
  fechaBooking: conGex('Fecha en que quedó confirmada la reserva.', 'Date the booking was confirmed.'),
  selectividad: conGex(
    'Revisión que asignó la DIAN: física, documental, escáner, escáner y física, automática u otra.',
    'Check assigned by the DIAN: physical, documentary, scanner, scanner and physical, automatic or other.',
  ),
  fechaInspeccion: conGex('Fecha de la inspección de la DIAN.', 'Date of the DIAN inspection.'),
  notas: txt(
    'Notas libres del trámite. Se escriben en la app.',
    'Free notes about the filing. They are typed in the app.',
  ),
};

export const AYUDA_ADUANA = {
  selectividadPorContenedor: txt(
    'Selectividad de la DIAN de cada contenedor, con su fecha. Del GEX FR08.',
    'DIAN selectivity of each container, with its date. From the GEX FR08.',
  ),
  facturaNumero: txt('Número de la factura.', 'Invoice number.'),
  facturaFecha: txt('Fecha de la factura.', 'Invoice date.'),
  facturaExp: txt(
    'Número de exportación del GEX al que pertenece la factura.',
    'GEX export number the invoice belongs to.',
  ),
  facturaToneladas: txt(
    'Toneladas de mineral de la factura (proforma), según el GEX.',
    'Tonnes of ore on the invoice (proforma), according to the GEX.',
  ),
  facturaValor: txt('Valor de la factura en su moneda (USD o COP).', 'Invoice amount in its currency (USD or COP).'),
  facturaOrigen: txt(
    'GEX = cargada del GEX FR08. Migración / app = la que usan los documentos DIAN.',
    'GEX = loaded from the GEX FR08. Migration / app = the one used by the DIAN documents.',
  ),
  comparado: txt(
    'En la app: lo que tiene el seguimiento de la exportación. En el GEX: lo que trae el GEX FR08. ≠ marca que no coinciden.',
    'In the app: what the export tracking has. In the GEX: what the GEX FR08 has. ≠ marks that they differ.',
  ),
} satisfies Record<string, Texto>;

// Financiacion de la exportacion, por campo (los del formulario y la vista).
export const AYUDA_FINANCIACION: Record<CampoDeFinanciacion | 'estado' | 'pagaAlFinanciador' | 'pasos', Texto> = {
  financiador: deStatusBlq(
    'Quién financia la venta (BLQ, carta de crédito…) o si es venta directa. Sale de la columna Invoice ID.',
    'Who finances the sale (BLQ, letter of credit…) or whether it is a direct sale. It comes from the Invoice ID column.',
  ),
  estado: txt(
    'En qué va la financiación (por financiar, en proceso, financiada, cerrada…). Lo calcula la app con los datos de esta sección.',
    'Where the financing stands (to be financed, in process, financed, closed…). The app works it out from the data in this section.',
  ),
  factura: deStatusBlq(
    'Factura al financiador, si ya existe (el número del Invoice ID).',
    'Invoice to the financer, if any (the number in the Invoice ID).',
  ),
  referencia: deStatusBlq(
    'Referencia del financiador: número BLQ o de la carta de crédito (columna BLQ/LC ID).',
    'Financer reference: BLQ or letter of credit number (BLQ/LC ID column).',
  ),
  bl: deStatusBlq('Número del BL según el archivo de financiación.', 'BL number according to the financing file.'),
  instrumento: txt(
    "Con qué tipo de crédito financia el banco esta exportación: carta de crédito (LC), buyer's credit (BC) o cash credit (CC). Se ofrecen los que da el financiador elegido.",
    "With which credit type the bank finances this export: letter of credit (LC), buyer's credit (BC) or cash credit (CC). The ones the chosen financer gives are offered.",
  ),
  banco: txt(
    'Banco que el financiador (Bluequest) da para esta exportación. Define la comisión inicial y el costo de fondos del TC calculation. Los bancos se agregan en Financiadores y cupos.',
    'Bank the financer (Bluequest) gives for this export. It sets the initial fee and cost of funds of the TC calculation. Banks are added in Financers and credit lines.',
  ),
  lote: txt(
    'Referencia del lote que da Bluequest o el custodio (Lot ref del TC calculation). Se escribe a mano.',
    'Lot reference given by Bluequest or the custodian (Lot ref of the TC calculation). Typed by hand.',
  ),
  financiadoUsd: txt(
    'Monto financiado en USD. Del Status BLQ (o del Intercompany Export Chain si el Status no lo trae); se edita aquí.',
    'Financed amount in USD. From the Status BLQ (or the Intercompany Export Chain if the Status does not have it); it is edited here.',
  ),
  porFinanciar: deStatusBlq(
    'Si todavía no se ha financiado (Pending to finance).',
    'Whether it has not been financed yet (Pending to finance).',
  ),
  desembolsoEsperado: txt(
    'Fecha esperada del desembolso. En BLQ y BLQM la calcula la app con el día del zarpe (↻); si no, la del Status BLQ o la escrita aquí.',
    'Expected disbursement date. For BLQ and BLQM the app works it out from the departure day (↻); otherwise, the Status BLQ one or the one typed here.',
  ),
  desembolso: deStatusBlq('Fecha real del desembolso del financiador.', 'Actual disbursement date from the financer.'),
  pagoInicialRecibido: deStatusBlq(
    'Si el financiador hizo el pago inicial.',
    'Whether the financer made the initial payment.',
  ),
  saldoPedido: deStatusBlq(
    'Si se le pidió el saldo al financiador, y cuándo.',
    'Whether the balance was requested from the financer, and when.',
  ),
  saldoPedidoEl: deStatusBlq(
    'Fecha en que se le pidió el saldo al financiador.',
    'Date the balance was requested from the financer.',
  ),
  saldoPagado: deStatusBlq(
    'Si el financiador pagó el saldo, cuánto y cuándo.',
    'Whether the financer paid the balance, how much and when.',
  ),
  saldoPagadoUsd: deStatusBlq(
    'Valor del saldo que pagó el financiador.',
    'Amount of the balance paid by the financer.',
  ),
  saldoPagadoEl: deStatusBlq('Fecha en que el financiador pagó el saldo.', 'Date the financer paid the balance.'),
  cambioBlPedidoEl: deStatusBlq('Fecha en que se pidió el cambio del BL.', 'Date the BL change was requested.'),
  blRecogidoEl: deStatusBlq('Fecha en que el financiador recogió el BL.', 'Date the financer picked up the BL.'),
  pagaAlFinanciador: txt(
    'Empresa que le paga al financiador: la que lo sigue en la cadena intercompany. La calcula la app.',
    'Company that pays the financer: the one after it in the intercompany chain. The app works it out.',
  ),
  valorCompradorUsd: deStatusBlq(
    'Valor que paga el comprador (o que se paga a la carta de crédito).',
    'Amount the buyer pays (or that is paid to the letter of credit).',
  ),
  pagoComprador: txt(
    'Fecha en que el comprador debe pagar. En BLQ y BLQM la calcula la app: días hábiles antes de la ETA a Mundra (↻); si no, la del Status BLQ o la escrita aquí.',
    'Date the buyer must pay. For BLQ and BLQM the app works it out: business days before the ETA in Mundra (↻); otherwise, the Status BLQ one or the one typed here.',
  ),
  facturaComprador: deStatusBlq('Factura al comprador, si ya existe.', 'Invoice to the buyer, if any.'),
  pagadoPorComprador: deStatusBlq('Si el comprador ya pagó.', 'Whether the buyer has paid.'),
  pagadoEl: deStatusBlq('Fecha en que pagó el comprador.', 'Date the buyer paid.'),
  valorTotalUsd: txt(
    'Valor total estimado de la exportación, de la hoja de proyecciones del Status BLQ; se edita aquí.',
    'Estimated total value of the export, from the projections sheet of the Status BLQ; it is edited here.',
  ),
  valorVentaUsd: txt(
    'Valor y fecha de venta proyectados, de la hoja de proyecciones del Status BLQ; se editan aquí.',
    'Projected sale value and date, from the projections sheet of the Status BLQ; they are edited here.',
  ),
  fechaVenta: txt(
    'Fecha de venta proyectada, de la hoja de proyecciones del Status BLQ; se edita aquí.',
    'Projected sale date, from the projections sheet of the Status BLQ; it is edited here.',
  ),
  cadena: deCadena('Empresas por las que pasa la venta, en orden.', 'Companies the sale goes through, in order.'),
  vendedor: deCadena('Empresa que vende en la cadena.', 'Company selling in the chain.'),
  comprador: deCadena('Comprador en la cadena.', 'Buyer in the chain.'),
  agenteDeCarga: deCadena('Agente de carga (forwarder).', 'Freight agent (forwarder).'),
  planta: deCadena('Planta de destino.', 'Destination facility.'),
  fijacionEl: deCadena('Fecha de fijación de precios.', 'Price fixing date.'),
  precioAuUsd: deCadena(
    'Precio fijado del oro (y de la plata), en USD por onza.',
    'Fixed price of gold (and silver), in USD per ounce.',
  ),
  precioAgUsd: deCadena('Precio fijado de la plata, en USD por onza.', 'Fixed price of silver, in USD per ounce.'),
  facturaGrupoUsd: deCadena(
    'Factura de la empresa exportadora del grupo, en USD.',
    'Invoice of the group exporting company, in USD.',
  ),
  valorCompletoUsd: deCadena('Valor completo en USD según la cadena.', 'Full value in USD according to the chain.'),
  facturaFinalUsd: deCadena('Factura final de la cadena, en USD.', 'Final invoice of the chain, in USD.'),
  estadoCadena: deCadena(
    'Estado de la cadena intercompany (completa, documentos pendientes…).',
    'Status of the intercompany chain (completed, documents pending…).',
  ),
  bancoComprador: deCadena('Banco del comprador.', 'Buyer’s bank.'),
  bancoRefineria: deCadena('Banco de la refinería.', 'Refinery bank.'),
  sofr: deCadena('Tasa SOFR usada; se escribe en porcentaje.', 'SOFR rate used; typed in percent.'),
  costoDeFondos: deCadena(
    'Costo de fondos del banco; se escribe en porcentaje.',
    'Bank cost of funds; typed in percent.',
  ),
  margenFinanciador: deCadena(
    'Margen del financiador; se escribe en porcentaje.',
    'Financer margin; typed in percent.',
  ),
  comisionInicial: deCadena('Comisión inicial; se escribe en porcentaje.', 'Initial fee; typed in percent.'),
  cargoFijoUsd: deCadena('Cargo fijo en USD.', 'Fixed fee in USD.'),
  tratamientoProvisionalUsd: txt(
    'Cargos provisionales de tratamiento en USD, del Status BLQ o del Intercompany Export Chain; se editan aquí.',
    'Provisional treatment charges in USD, from the Status BLQ or the Intercompany Export Chain; they are edited here.',
  ),
  tratamientoFinalUsd: deCadena('Cargos finales de tratamiento en USD.', 'Final treatment charges in USD.'),
  muestreoFinalUsd: deCadena('Costo final de muestreo en USD.', 'Final sampling cost in USD.'),
  otrosCostosUsd: deCadena('Otros costos en USD.', 'Other costs in USD.'),
  comentarios: txt(
    'Comentarios del Status BLQ y del Intercompany Export Chain; se editan aquí.',
    'Comments from the Status BLQ and the Intercompany Export Chain; they are edited here.',
  ),
  pasos: txt(
    'Pasos de la cadena (BL inicial, cambios de BL, factura final, correos…): ✓ hecho, ○ pendiente. Del Intercompany Export Chain.',
    'Chain steps (initial BL, BL switches, final invoice, emails…): ✓ done, ○ pending. From the Intercompany Export Chain.',
  ),
};

// Datos que se piden al generar los documentos (paquete documental).
export const AYUDA_DOCUMENTOS = {
  trm: txt(
    'Tasa de cambio (pesos por dólar) para los valores en pesos de los documentos. Se propone la de la última instrucción de facturación o de los últimos documentos de aduana; se puede cambiar.',
    'Exchange rate (pesos per dollar) for the peso amounts in the documents. The one from the last invoicing instruction or customs documents is suggested; it can be changed.',
  ),
  precioOro: txt(
    'Precio del oro en dólares por onza troy para los documentos. Se propone el último usado; el precio pagable es el 83 %.',
    'Gold price in dollars per troy ounce for the documents. The last one used is suggested; the payable price is 83 %.',
  ),
  precioPlata: txt(
    'Precio de la plata en dólares por onza troy para los documentos. Se propone el último usado; el precio pagable es el 71 %.',
    'Silver price in dollars per troy ounce for the documents. The last one used is suggested; the payable price is 71 %.',
  ),
  incoterm: txt(
    'Condición de entrega que se imprime en los documentos. Se propone la última usada (o CIF Mundra-India).',
    'Delivery terms printed on the documents. The last one used (or CIF Mundra-India) is suggested.',
  ),
  facturaPorMina: txt(
    'Número de la factura de esta mina para los documentos de aduana. Se propone el que ya tenga la exportación; al generar queda guardado.',
    'Invoice number of this mine for the customs documents. The one the export already has is suggested; it is saved on generating.',
  ),
  flete: txt(
    'Flete de un contenedor en dólares (por defecto 4.500) para el cálculo CIF.',
    'Freight of one container in dollars (4,500 by default) for the CIF calculation.',
  ),
  bolsasFlete: txt(
    'Entre cuántas bolsas se divide el flete de un contenedor (por defecto 30: 150 USD por Big Bag). No es lo mismo que los 28 Big Bags de un contenedor.',
    'How many bags one container’s freight is split across (30 by default: 150 USD per Big Bag). It is not the same as the 28 Big Bags in a container.',
  ),
  seguro: txt(
    'Seguro por cada mil del valor en pesos (por defecto 0,5 por mil).',
    'Insurance per thousand of the peso value (0.5 per thousand by default).',
  ),
  fechaFactura: txt(
    'Fecha de las facturas que se imprime en los documentos. Se propone la ya guardada o la de hoy.',
    'Invoice date printed on the documents. The saved one or today’s is suggested.',
  ),
  fechaCarta: txt(
    'Fecha con la que salen las cartas. Por defecto, hoy.',
    'Date the letters are issued with. Today by default.',
  ),
} satisfies Record<string, Texto>;

// Asignacion de contenedores por reserva (exportaciones propias).
export const AYUDA_ASIGNACION = {
  // Mineros (decision 0073).
  reglaMineros: txt(
    'Mineros: entran los Big Bags con todos sus resultados (no parciales), estén donde estén, en orden de producción (el primero que se produjo sale primero). Cada contenedor empieza con los 4 de alto tenor más antiguos y se llena con bajo tenor mientras el promedio (oro ÷ toneladas secas) no baje de 800 g/t; si no, entra otro de alto tenor. Hasta 1000 g/t en casos puntuales. Se arma cuando llega a 27 toneladas húmedas; si no alcanza, sale una alerta con lo que falta.',
    'Mineros: Big Bags with all their results (not partial), wherever they are, in order of production (first produced, first out). Each container starts with the 4 oldest high-grade bags and is filled with low grade while the average (gold ÷ dry tons) does not drop below 800 g/t; otherwise another high-grade bag goes in. Up to 1000 g/t in specific cases. It is built once it reaches 27 wet tons; if there is not enough, an alert says what is missing.',
  ),
  tenorMineros: txt(
    'Gramos de oro por tonelada seca del Big Bag. "alto" = Big Bag de alto tenor (MABBHG).',
    'Grams of gold per dry ton of the Big Bag. "high" = high-grade Big Bag (MABBHG).',
  ),
  reserva: txt(
    'Reserva (booking) de la naviera vinculada a esta exportación, en Exportaciones → Reservas.',
    'Shipping line booking linked to this export, in Exports → Bookings.',
  ),
  salida: txt(
    'Fecha de salida (ETD) que dice la reserva y cuántos días faltan.',
    'Departure date (ETD) stated in the booking and how many days are left.',
  ),
  completos: txt(
    'Contenedores con sus 28 Big Bags frente a los que pide la reserva. Lo calcula la app.',
    'Containers with their 28 Big Bags against those the booking asks for. The app works it out.',
  ),
  disponibles: txt(
    'Big Bags de este exportador que están disponibles en bodega para llenar contenedores. Los cuenta la app.',
    'Big Bags from this exporter available in the warehouse to fill containers. The app counts them.',
  ),
  automatica: txt(
    'Marcada, la app arma sola un contenedor cada vez que haya 28 Big Bags disponibles, sin pasar de 28 toneladas húmedas.',
    'Ticked, the app builds a container by itself whenever 28 Big Bags are available, without going over 28 wet tonnes.',
  ),
  recoleccion: txt('Recolección (Shipment #) con la que llegó la bolsa.', 'Pickup (Shipment #) the bag arrived with.'),
  entradaBodega: txt('Fecha en que la bolsa entró a la bodega.', 'Date the bag entered the warehouse.'),
  tonHumedas: txt('Peso húmedo de la bolsa en toneladas.', 'Wet weight of the bag in tonnes.'),
  tonSecas: txt('Peso seco de la bolsa en toneladas.', 'Dry weight of the bag in tonnes.'),
} satisfies Record<string, Texto>;

// Expediente de la exportacion.
export const AYUDA_EXPEDIENTE = {
  archivo: txt(
    'Documento que se guarda en el expediente de la exportación (BL, facturas, certificados…). Lo sube un editor o un administrador.',
    'Document saved in the export’s file (BL, invoices, certificates…). An editor or an administrator uploads it.',
  ),
  categoria: txt(
    'Qué tipo de documento es, para ordenarlo en el expediente. Lo elige quien lo sube.',
    'What kind of document it is, to sort it in the file. Whoever uploads it chooses.',
  ),
} satisfies Record<string, Texto>;

// Cargar cambios de exportaciones desde Excel.
export const AYUDA_CARGA_DE_CAMBIOS = {
  archivo: txt(
    'El Excel para editar que baja del seguimiento, o el Status Concentrates Shipments con la hoja Tracking Status. Nada se guarda hasta que confirmes.',
    'The Editable Excel downloaded from tracking, or the Status Concentrates Shipments with the Tracking Status sheet. Nothing is saved until you confirm.',
  ),
  crearNuevas: txt(
    'Marcada, las exportaciones del archivo que no existen en la app se crean. Sin marcar, no se crea ninguna.',
    'Ticked, the exports in the file that do not exist in the app are created. Unticked, none is created.',
  ),
  fecha: txt('Cuándo se cargó el archivo.', 'When the file was uploaded.'),
  nombre: txt('Nombre del archivo cargado.', 'Name of the uploaded file.'),
  estado: txt(
    'Cómo quedó la carga (en revisión, aplicada, con error…).',
    'How the upload ended (under review, applied, with errors…).',
  ),
  filas: txt('Filas que se leyeron del archivo.', 'Rows read from the file.'),
  creadas: txt('Exportaciones nuevas que se crearon.', 'New exports that were created.'),
  actualizadas: txt('Exportaciones que cambiaron.', 'Exports that changed.'),
} satisfies Record<string, Texto>;
